import React from 'react';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';


const ActivitySidebar = ({
  isCollapsed,
  onToggleCollapse,
  activities,
  favorites,
  selectedCategory,
  searchTerm,
  onCategoryChange,
  onSearchChange,
  onToggleFavorite,
  onDragStart
}) => {
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'activity', label: 'Activities' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'food', label: 'Food & Dining' },
    { value: 'transport', label: 'Transportation' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'sightseeing', label: 'Sightseeing' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'entertainment', label: 'Entertainment' }
  ];

  const getCategoryIcon = (category) => {
    const icons = {
      activity: 'Waves',
      accommodation: 'Hotel',
      food: 'Utensils',
      transport: 'Car',
      wellness: 'Heart',
      sightseeing: 'Camera',
      shopping: 'ShoppingBag',
      entertainment: 'Music'
    };
    return icons?.[category] || 'MapPin';
  };

  const getCategoryColor = (category) => {
    const colors = {
      activity: 'text-teal-600 bg-teal-50',
      accommodation: 'text-purple-600 bg-purple-50',
      food: 'text-orange-600 bg-orange-50',
      transport: 'text-blue-600 bg-blue-50',
      wellness: 'text-pink-600 bg-pink-50',
      sightseeing: 'text-purple-600 bg-purple-50',
      shopping: 'text-green-600 bg-green-50',
      entertainment: 'text-yellow-600 bg-yellow-50'
    };
    return colors?.[category] || 'text-gray-600 bg-gray-50';
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700' },
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
      available: { label: 'Available', color: 'bg-blue-100 text-blue-700' }
    };
    return badges?.[status] || badges?.available;
  };

  const filteredActivities = activities?.filter(activity => {
    const matchesCategory = selectedCategory === 'all' || activity?.category === selectedCategory;
    const matchesSearch = activity?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         activity?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const favoriteActivities = activities?.filter(act => favorites?.includes(act?.id));

  if (isCollapsed) {
    return (
      <div className="w-16 bg-card border-r border-border flex flex-col items-center py-4 gap-4">
        <button
          onClick={onToggleCollapse}
          className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          aria-label="Expand sidebar"
        >
          <Icon name="ChevronRight" size={20} />
        </button>
        <div className="flex flex-col gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Sparkles" size={18} className="text-primary" />
          </div>
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="Heart" size={18} className="text-secondary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-r border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">Activities</h2>
          <button
            onClick={onToggleCollapse}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Collapse sidebar"
          >
            <Icon name="ChevronLeft" size={18} />
          </button>
        </div>
        
        {/* Search */}
        <div className="relative mb-3">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full h-10 pl-10 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Category Filter */}
        <Select
          options={categoryOptions}
          value={selectedCategory}
          onChange={onCategoryChange}
          placeholder="Select category"
        />
      </div>
      {/* Favorites Section */}
      {favoriteActivities?.length > 0 && (
        <div className="border-b border-border flex-shrink-0">
          <div className="p-4 pb-2">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Heart" size={16} className="text-secondary" />
              <h3 className="text-sm font-semibold text-foreground">Favorites</h3>
              <span className="text-xs text-muted-foreground">({favoriteActivities?.length})</span>
            </div>
          </div>
          <div className="px-4 pb-4 space-y-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
            {favoriteActivities?.map(activity => (
              <div
                key={activity?.id}
                draggable
                onDragStart={(e) => onDragStart(e, activity)}
                className="bg-background rounded-lg border border-border p-3 cursor-move hover:border-primary/50 transition-all group"
              >
                <div className="flex items-start gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getCategoryColor(activity?.category)}`}>
                    <Icon name={getCategoryIcon(activity?.category)} size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground truncate">{activity?.title}</h4>
                    <p className="text-xs text-muted-foreground truncate">{activity?.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-semibold text-accent">₱{activity?.cost?.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">• {activity?.duration}h</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onToggleFavorite(activity?.id)}
                    className="flex-shrink-0"
                  >
                    <Icon name="Heart" size={14} className="text-secondary fill-secondary" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Activities List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">All Activities</h3>
            <span className="text-xs text-muted-foreground">({filteredActivities?.length})</span>
          </div>
          
          {filteredActivities?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Search" size={32} className="mx-auto mb-2 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">No activities found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredActivities?.map(activity => {
                const statusBadge = getStatusBadge(activity?.status);
                const isFavorite = favorites?.includes(activity?.id);
                
                return (
                  <div
                    key={activity?.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, activity)}
                    className="bg-background rounded-lg border border-border p-3 cursor-move hover:border-primary/50 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getCategoryColor(activity?.category)}`}>
                        <Icon name={getCategoryIcon(activity?.category)} size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-foreground">{activity?.title}</h4>
                          <button
                            onClick={() => onToggleFavorite(activity?.id)}
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Icon 
                              name="Heart" 
                              size={14} 
                              className={isFavorite ? 'text-secondary fill-secondary' : 'text-muted-foreground'}
                            />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{activity?.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-accent">₱{activity?.cost?.toLocaleString()}</span>
                            <span className="text-xs text-muted-foreground">• {activity?.duration}h</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge?.color}`}>
                            {statusBadge?.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Tips */}
      <div className="p-4 border-t border-border bg-accent/5 flex-shrink-0">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={14} className="text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            Drag activities to time slots on the timeline to build your itinerary
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivitySidebar;